import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

/**
 * The document chain — Quotation/Proforma/direct sale → Tax Invoice.
 *
 * Shared by QuotationsPage, ProformaPage, and InvoicesPage (for a walk-in
 * "New Invoice" with no quotation/proforma behind it).
 *
 * "Create Tax Invoice" raises an Invoice Request and generates a DRAFT invoice
 * from it — editable, unnumbered, not yet posted. The client asked for the
 * invoice to stay editable after creation; a posted GST invoice cannot be, so
 * the draft is where "final changes" happen. `finalizeTaxInvoice` is the second,
 * explicit step: it allocates the GST number, posts both ledgers, and hands the
 * order to Dispatch.
 *
 * The advance-payment gate (src/shared/billing/advance-gate.js) is NOT enforced
 * on any route. It is fully unit-tested but unreferenced since the owner decided
 * (commit 6bfda8df, 2026-10-14) that a credit dispatch is notified to Admins and
 * Superadmins rather than blocked — see src/shared/dispatch/credit-notify.js. So
 * no step here can expect a 422; nothing refuses a finalize for an unpaid advance,
 * and the UI must not wait for one.
 */

interface SourceOrderLine {
  productId?: string | { _id?: string } | null;
  description?: string;
  /** Per-line customisation note from the quotation form. */
  notes?: string;
  productCode?: string;
  unit?: string;
  quantity?: number;
  unitPrice?: number;
  discount?: number;
  discountType?: 'fixed' | 'percent';
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  hsnSac?: string;
  packSize?: string;
  qtyPerPack?: number;
  totalPackSize?: string;
  volumeLiters?: number;
}

interface SourceOrder {
  _id: string;
  orderNumber?: string;
  customerName?: string;
  customerId?: { _id?: string } | string | null;
  crmDealId?: string | null;
  currency?: string;
  notes?: string;
  dueDate?: string | null;
  shippingAddress?: Record<string, unknown> | null;
  billingAddress?: Record<string, unknown> | null;
  shippingCharge?: number;
  freightCharges?: number;
  /** Round-off paise adjustment stored on the source quotation/proforma. */
  roundOff?: number;
  /** Commercial terms from the quotation (array of objects or a joined string). */
  terms?: unknown;
  paymentTerms?: string;
  lines?: SourceOrderLine[];
}

/** requestLineSchema (src/api-invoice-requests/models.js) only carries one
 * percent discount field — a fixed-amount line discount is converted here. */
function toRequestItem(line: SourceOrderLine) {
  const quantity = Number(line.quantity) || 0;
  const rate = Number(line.unitPrice) || 0;
  const gross = quantity * rate;
  const discountPercent = line.discountType === 'percent'
    ? Number(line.discount) || 0
    : gross > 0 ? ((Number(line.discount) || 0) / gross) * 100 : 0;
  const productId = typeof line.productId === 'object' ? line.productId?._id : line.productId;

  return {
    catalogItemId: productId || null,
    code: line.productCode || '',
    name: line.description || line.productCode || 'Item',
    description: line.description || '',
    // Without this the note dies at the request hop and never reaches the
    // invoice line, however carefully it was typed on the quotation.
    notes: line.notes || '',
    unit: line.unit || '',
    // Same gap as notes above, just never noticed because HSN/pack columns
    // didn't exist on the printed invoice until today — without these, the
    // request (requestLineSchema, api-invoice-requests/models.js, which
    // does declare all five) always saved them blank regardless of what the
    // source quotation line actually had.
    hsnSac: line.hsnSac || '',
    packSize: line.packSize || '',
    qtyPerPack: Number(line.qtyPerPack) || 0,
    totalPackSize: line.totalPackSize || '',
    volumeLiters: Number(line.volumeLiters) || 0,
    quantity,
    rate,
    discountPercent,
    // Intra-state (CGST+SGST) and inter-state (IGST) are alternatives, never
    // both populated on a real line — summing is safe. Same pattern as
    // computeLine() in src/shared/documents/totals.js.
    taxRate: (Number(line.cgstRate) || 0) + (Number(line.sgstRate) || 0) + (Number(line.igstRate) || 0),
  };
}

/** What the server sends back when the advance has not been received. */
export interface AdvanceBlocked {
  code: 'ADVANCE_NOT_RECEIVED';
  error: string;
  /** Whether ANYONE can release this — not whether the current user can. */
  overridable: boolean;
  requiredAdvance: number;
  shortfall: number;
}

export interface AdvanceRequirement {
  advancePercent: number;
  requiredAdvance: number;
  receipted: number;
}

export interface ConvertResult<T> {
  ok: boolean;
  advanceRequirement?: AdvanceRequirement | null;
  overrideUsed?: boolean;
  invoice?: T;
}

/** What "Create Tax Invoice" produced — a DRAFT invoice to review and finalize. */
export interface TaxInvoiceResult {
  invoiceId: string;
  draft: boolean;
  invoiceNumber: string;
  result: ConvertResult<Record<string, unknown>>;
  needsApproval?: boolean;
  message?: string;
}

/** Narrow an axios failure to the advance block, or null if it was anything else. */
export function asAdvanceBlocked(err: unknown): AdvanceBlocked | null {
  const data = (err as { response?: { status?: number; data?: AdvanceBlocked } })?.response;
  if (data?.status === 422 && data.data?.code === 'ADVANCE_NOT_RECEIVED') return data.data;
  return null;
}

/**
 * Narrow an axios failure to "you may override, but must say why" — hit
 * instead of ADVANCE_NOT_RECEIVED when the caller already holds
 * invoice.override_advance, so the shortfall never blocks them outright; it
 * just refuses a blank reason. Shaped like AdvanceBlocked so the caller can
 * reuse the exact same "reason" prompt for both.
 */
export function asOverrideReasonRequired(err: unknown): AdvanceBlocked | null {
  const data = (err as { response?: { status?: number; data?: { code?: string; error?: string; overridable?: boolean; requiredAdvance?: number; shortfall?: number } } })?.response;
  if (data?.status === 400 && data.data?.code === 'OVERRIDE_REASON_REQUIRED') {
    return {
      code: 'ADVANCE_NOT_RECEIVED',
      error: data.data.error || '',
      overridable: data.data.overridable ?? true,
      requiredAdvance: data.data.requiredAdvance ?? 0,
      shortfall: data.data.shortfall ?? 0,
    };
  }
  return null;
}

/** True when the source has already been invoiced — a 409, not a failure to retry. */
export function asAlreadyInvoiced(err: unknown): { invoiceId: string; error: string } | null {
  const res = (err as { response?: { status?: number; data?: { code?: string; invoiceId?: string; error?: string } } })?.response;
  if (res?.status === 409 && res.data?.code === 'ALREADY_INVOICED') {
    return { invoiceId: res.data.invoiceId || '', error: res.data.error || 'Already invoiced.' };
  }
  return null;
}

export function useDocumentChain() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['quotations'] });
    qc.invalidateQueries({ queryKey: ['proforma'] });
    qc.invalidateQueries({ queryKey: ['invoices'] });
    qc.invalidateQueries({ queryKey: ['invoice-requests'] });
    qc.invalidateQueries({ queryKey: ['pipeline'] });
  };

  /**
   * Quotation, Proforma, or direct sale → Tax Invoice, in one click: raises
   * the Invoice Request, then immediately generates the invoice from it.
   *
   * `overrideReason` is only read by the server when the caller holds
   * `invoice.override_advance` AND the advance is actually short. Sending it
   * speculatively is harmless; sending it blank when an override IS needed is
   * refused, because an override nobody can audit is the same as no gate.
   */
  const createTaxInvoice = useMutation({
    mutationFn: async ({ sourceId }: { sourceId: string }): Promise<TaxInvoiceResult> => {
      const { data: source } = await api.get<SourceOrder>(`/api/sales-ops/orders/${sourceId}`);

      const { data: request } = await api.post<{ ok: boolean; id: string; number: string; needsApproval?: boolean }>(
        '/api/invoice-requests',
        {
          clientId: typeof source.customerId === 'object' ? source.customerId?._id : source.customerId,
          clientName: source.customerName || '',
          sourceQuotationId: source._id,
          sourceQuotationNumber: source.orderNumber || '',
          sourceDealId: source.crmDealId || null,
          items: (source.lines || []).map(toRequestItem),
          currency: source.currency || 'INR',
          notes: source.notes || '',
          dueDate: source.dueDate || null,
          shippingAddress: source.shippingAddress || null,
          billingAddress: source.billingAddress || null,
          shippingCharge: source.shippingCharge ?? source.freightCharges ?? 0,
          // Carry round-off, terms, and payment terms from the source document
          // so the generated invoice matches the quotation the customer signed off on.
          roundOff: source.roundOff ?? 0,
          terms: source.terms ?? '',
          paymentTerms: source.paymentTerms ?? '',
        },
      );

      if (request.needsApproval) {
        return {
          invoiceId: '',
          draft: false,
          invoiceNumber: '',
          needsApproval: true,
          message: 'Invoice request submitted for approval. Super Admin, Admin, or Department Admin will review and approve it.',
          result: { ok: true },
        };
      }

      const { data } = await api.post(`/api/invoice-requests/${request.id}/generate-invoice`, {});
      const result = data as ConvertResult<Record<string, unknown>> & { invoiceId?: string; invoiceNumber?: string; draft?: boolean };
      return {
        invoiceId: result.invoiceId || '',
        draft: result.draft !== false,
        invoiceNumber: result.invoiceNumber || '',
        result,
      };
    },
    onSuccess: invalidate,
  });

  /**
   * Finalize & Post a draft invoice, by its invoice id. Allocates the GST
   * number, posts the ledgers, hands the order to Dispatch. The advance-payment
   * gate bites here — a 422 ADVANCE_NOT_RECEIVED carries the shortfall and
   * whether anyone can release it.
   */
  const finalizeTaxInvoice = useMutation({
    mutationFn: async ({ invoiceId, overrideReason }: { invoiceId: string; overrideReason?: string }) => {
      const { data } = await api.post(
        `/api/invoice-requests/finalize-by-invoice/${invoiceId}`,
        overrideReason ? { advanceOverrideReason: overrideReason } : {},
      );
      return data as { ok: boolean; invoiceNumber?: string; alreadyFinalized?: boolean };
    },
    onSuccess: invalidate,
  });

  return { createTaxInvoice, finalizeTaxInvoice };
}
